import * as React from 'react';
import qs from "query-string";
import uniqBy from "lodash-es/uniqBy";
import { Link, useLocation } from "react-router-dom";
import { Authorization } from "../../misc/components/Authorization";
import { HamburgerMenu } from '../../misc/components/HamburgerMenu';
import { formatDateTime } from "../../misc/components/Formatters/Formatters";
import { PermissionEnum } from '../../misc/api/ApiPermissionEnum';
import { Column, Table } from '../../Components/QueryTable';
import { useQuery } from '../../Components/Query';
import { MatchSortHeader } from '../../Components/MatchSortHeader';
import { OscillometrySession, WebPortalClient } from 'webportalclient';
import { useCurrentStudyConfigurationContext } from '../../Core/CurrentStudyConfigurationContext';
import { SortHeader } from '../../Components/SortHeader';
import { useReferenceContextProvider } from '../../Core/ReferenceContextProvider';
import { DateMatchSortHeader } from '../../Components/DateMatchSortHeader';
import LoadingWithMessage from '../../misc/components/LoadingWithMessage';
import { useSecurityContext } from '../../Core/SecurityContext';
import { AnalysisAcceptabilityStatus } from '../../misc/components/analysis/AnalysisAcceptabilityStatus';
import { useWebPortalApiClient } from '../../misc/api/WebPortalTokenClient';
import { SessionTypes } from '../../misc/api/ApiSessionTypes';


export interface OscillometrySessionsListTableProps {
}

export const OscillometrySessionsListTable = (props: OscillometrySessionsListTableProps) => {
    const { data, applyFilters, filters } = useQuery();
    const { study } = useCurrentStudyConfigurationContext();
    const { currentUserPermissions } = useSecurityContext();
    const { subjectTypes, overreadStatuses } = useReferenceContextProvider();
    const location = useLocation();

    const client = useWebPortalApiClient();
    const [siteOptions, setSiteOptions] = React.useState([]);
    const [subjectOptions, setSubjectOptions] = React.useState([]);
    const [visitOptions, setVisitOptions] = React.useState([]);
    const [sessionOptions, setSessionOptions] = React.useState([]);

    const [analysts, setAnalysts] = React.useState([]);

    const statusesOptions = overreadStatuses.map(os => ({
        value: os.id,
        label: os.name
    }))

    React.useEffect(() => {
        const getSites = async () => {

            const sites = await client.client.getSites("id,number", `study/id eq ${study.id}`, "number");
            const options = sites.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSiteOptions(options);
        };


        const getSubjects = async () => {

            const subjects = await client.client.getSubjects("id,number", `site/study/id eq ${study.id}`, "number");
            const options = subjects.value.map(s => ({
                value: s.id,
                label: s.number,
            }));

            setSubjectOptions(options);
        };

        const getVisits = async () => {

            const visits = await client.client.getStudyScheduleVisits("id,label", `studySchedule/studyId eq ${study.id}`, "label", "studySchedule");
            const options = visits.value.map(s => ({
                value: s.label,
                label: s.label,
            }));

            setVisitOptions(options);
        };

        const getSessions = async () => {

            const sessions = await client.client.getStudyScheduleSessions("id,label", `studyScheduleVisit/studySchedule/studyId eq ${study.id} and sessionTypeId eq '${SessionTypes.Oscillometry}'`, "label", "studyScheduleVisit($expand=studySchedule)");

            const options = sessions.value.map(s => ({
                value: s.label,
                label: s.label,
            }));

            setSessionOptions(options);
        };

        const getAnalysts = async () => {
            const analysts = await client.client.getOscillometryAnalysisSets("id, analyst", `session/visit/subject/site/studyId eq ${study.id}`, "analyst");

            const options = uniqBy(analysts.value, o => o.analyst).filter(o => o.analyst).map(s => ({
                value: s.analyst.replace(/'/g, "''"),
                label: s.analyst,
            }));

            setAnalysts(options);
        };

        getSites();
        getSubjects();
        getVisits();
        getSessions();
        getAnalysts();

        const queryString = qs.parse(location.search);

        if (queryString.statusId) {
            applyFilters([
                ...filters,
                {
                    field: "status",
                    condition: "eq",
                    values: [queryString.statusId],
                }]);
        }
    }, [location.search]);

    const clearAllFilters = () => {
        applyFilters([]);
    }

    if (subjectTypes.length === 0) {
        return (<LoadingWithMessage message={`Loading Oscillometry Sessions...`} />);
    }

    const formatAnalyst = analyst => analyst ? analyst : "-";

    const formatStatus = (status: string) => {
        return (<>
            <AnalysisAcceptabilityStatus value={status} /><br />
        </>);
    }

    const RenderHamburgerMenu = (row: OscillometrySession) => {
        return (
            row.analysisSetId && 
            <HamburgerMenu>
                <Authorization permissionId={PermissionEnum.AccessSingleOscillometrySession} currentPermissions={currentUserPermissions}
                    studyId={study.id}>
                    <li>
                        <Link to={`/Oscillometry/Analysis/${row.analysisSetId}/view`}>View</Link>
                    </li>
                </Authorization>
            </HamburgerMenu>
        );
    }

    const ViewOverreaderName = () => {
        return currentUserPermissions.some(x => (x.Id === PermissionEnum.ViewOverreaderName));
    }

    return (
        <>
            <div className="row">
                <div className="col-md-6">
                    <h2 className="text-primary">Oscillometry Sessions</h2>
                </div>
                <div className="col-md-6">
                    <div className="h2 pull-right">
                        <button className="btn btn-primary" onClick={clearAllFilters}>
                            Clear All Filters
                        </button>
                    </div>
                </div>
            </div>
            <div className="row">
                <div className="col-md-12">
                    <div className="ibox">
                        <div className="ibox-content">
                            <div className="table-responsive">
                                <Table data={data}>
                                    <Column field="visit" header={<MatchSortHeader title="Site ID" field="visit/subject/site/id" options={siteOptions} sortField="visit/subject/site/number" />} formatter={visit => visit.subject.site.number} />
                                    <Column field="visit" header={<MatchSortHeader title="Subject ID" field="visit/subject/id" options={subjectOptions} sortField="visit/subject/number" />} formatter={visit => visit.subject.number} />
                                    <Column field="visit" header={<MatchSortHeader title="Visit" field="visit/name" options={visitOptions} sortField="visit/name" />} formatter={visit => visit.name} />
                                    <Column field="name" header={<MatchSortHeader title="Session" field="name" options={sessionOptions} sortField="name" />} formatter={name => name} />
                                    <Column field="occurred" header={<DateMatchSortHeader title="Session Start" field="occurred" sortField="occurred" />} formatter={occurred => formatDateTime(occurred)} />
                                    <Column field="testsCount" header={<SortHeader title="Tests" field="testsCount" />} />
                                    <Column field="technician" header={<SortHeader title="Technician" field="technician" />} formatter={technician => technician} />
                                    <Column field="status" header={<MatchSortHeader title="Status" field="status" options={statusesOptions} sortField="status" />} formatter={analysisSet => formatStatus(analysisSet)} />
                                    { ViewOverreaderName() ?
                                        <Column field="overreader" header={<MatchSortHeader title="Overreader" field="overreader" options={analysts} sortField="overreader" />} formatter={analyst => formatAnalyst(analyst)} />
                                        : <></>
                                    }
                                    <Column field="" formatter={RenderHamburgerMenu} />
                                </Table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
}
